/**
 * Cliente Supabase otimizado para o Marketplace
 * Remove funcionalidades não usadas para reduzir bundle size
 * 
 * SEGURANÇA:
 * - Usa anon key (público) - seguro para marketplace
 * - Sem persistência de sessão - reduz superfície de ataque
 * - Validação de variáveis de ambiente
 * 
 * PERFORMANCE:
 * - Singleton pattern - uma única instância
 * - Sem realtime - reduz overhead
 * - Lazy initialization - carrega apenas quando necessário
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Database } from './types';

let marketplaceSupabaseInstance: SupabaseClient<Database> | null = null;
let initializationError: Error | null = null;

/**
 * Inicializa o cliente marketplace de forma segura
 * Cacheia erros para evitar múltiplas tentativas
 */
function initializeMarketplaceClient(): SupabaseClient<Database> {
  // Retorna instância existente (singleton)
  if (marketplaceSupabaseInstance) {
    return marketplaceSupabaseInstance;
  }

  // Se já tentou e falhou, lança o erro cacheado
  if (initializationError) {
    throw initializationError;
  }

  try {
    // ✅ SEGURANÇA: Forçar uso de variáveis de ambiente (sem fallback hardcoded)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    // Validação de variáveis de ambiente
    if (!supabaseUrl || !supabaseAnonKey) {
      // Em produção, tentar usar window.__ENV__ como fallback (injetado pelo Vercel)
      const windowEnv = (window as any).__ENV__;
      const fallbackUrl = windowEnv?.VITE_SUPABASE_URL;
      const fallbackKey = windowEnv?.VITE_SUPABASE_ANON_KEY;
      
      if (!fallbackUrl || !fallbackKey) {
        console.error('[MarketplaceClient] Environment variables missing:', {
          hasUrl: !!supabaseUrl,
          hasKey: !!supabaseAnonKey,
          hasFallbackUrl: !!fallbackUrl,
          hasFallbackKey: !!fallbackKey,
          env: import.meta.env
        });
        
        throw new Error(
          '🔒 SECURITY ERROR: Missing Supabase environment variables. ' +
          'Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in Vercel environment variables. ' +
          'Never hardcode API keys in source code.'
        );
      }
      
      // Usar fallback
      console.warn('[MarketplaceClient] Using fallback environment variables from window.__ENV__');
      return createMarketplaceClient(fallbackUrl, fallbackKey);
    }

    // Validação básica de formato
    if (!supabaseUrl.startsWith('https://')) {
      throw new Error('VITE_SUPABASE_URL must be a valid HTTPS URL');
    }

    return createMarketplaceClient(supabaseUrl, supabaseAnonKey);
  } catch (error) {
    // Cacheia o erro para evitar múltiplas tentativas
    initializationError = error instanceof Error ? error : new Error(String(error));
    throw initializationError;
  }
}

/**
 * Cria o cliente Supabase com as configurações otimizadas
 */
function createMarketplaceClient(url: string, key: string): SupabaseClient<Database> {
  /**
   * Cliente otimizado para marketplace público
   * - persistSession: false - Não armazena sessão (marketplace é público)
   * - autoRefreshToken: false - Não precisa refresh (sem auth)
   * - detectSessionInUrl: false - Não detecta sessão na URL
   */
  marketplaceSupabaseInstance = createClient<Database>(url, key, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'imobpanda-marketplace-token', // ✅ Unique storage key to avoid conflicts
    },
    global: {
      headers: {
        'x-client-info': 'imobpanda-marketplace',
      },
    },
  });

  return marketplaceSupabaseInstance;
}

/**
 * Getter para o cliente marketplace
 * Uso recomendado quando você precisa de controle explícito
 * 
 * @throws {Error} Se variáveis de ambiente não estiverem configuradas
 * @example
 * const client = getMarketplaceSupabase();
 * const { data } = await client.from('imoveis').select('*');
 */
export const getMarketplaceSupabase = (): SupabaseClient<Database> => {
  return initializeMarketplaceClient();
};

/**
 * Cliente marketplace pré-inicializado
 * Uso direto para conveniência (inicializa no primeiro acesso)
 * 
 * @example
 * const { data } = await marketplaceSupabase.from('imoveis').select('*');
 */
export const marketplaceSupabase = getMarketplaceSupabase();
